import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AxiosResponse } from "axios";
import { Check, Calendar, GraduationCap } from "lucide-react";
import { Loader } from "semantic-ui-react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { notification } from "../../utils/helpers";

export const RenewalOffer = ({ currentUser }) => {
  const [quote, setQuote] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    getRequest("/api/checkout/subscription_overrides")
      .then((response: AxiosResponse) => {
        setQuote(response.data?.quotation);
        setSubscription(response.data?.subscription);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleRenewalOffer = () => {
    setSubmitting(true);
    postRequest("/api/checkout/subscription_overrides", {
      subscription_renewal_override: {
        merchant_subscription_id: subscription?.id,
        yearly_amount_cents: quote?.fractional,
        currency: quote?.currency_code,
      },
    })
      .then(() => {
        setSubmitting(false);
        setShowSuccess(true);
      })
      .catch(() => {
        setSubmitting(false);
        notification(
          "There was an error claiming this discount. Please contact support.",
          "error",
        );
      });
  };

  return (
    <div className="renewal-offer">
      {loading ? (
        <div className="ui active inverted dimmer">
          <div className="ui text loader">Loading</div>
        </div>
      ) : (
        <main className="renewal-offer__main">
          {!showSuccess && !subscription?.has_renewal_offer ? (
            <div
              className={`renewal-offer__offer-view ${fadeOut ? "renewal-offer__offer-view--fade-out" : ""}`}
            >
              <div className="renewal-offer__header">
                <h1 className="renewal-offer__title">
                  Let's keep the good times rolling, {currentUser.first_name}.
                </h1>
                <p className="renewal-offer__subtitle">
                  You're about to lock in your special renewal rate. Smart move.
                </p>
              </div>

              <div className="renewal-offer__card">
                <div className="renewal-offer__badge">Exclusive Offer</div>

                <div className="renewal-offer__pricing">
                  <p className="renewal-offer__pricing-label">
                    The Cheeky renewal Offer
                  </p>
                  <div className="renewal-offer__pricing-amount">
                    <span className="renewal-offer__price">
                      {quote?.currency}
                      {quote?.formatted_amount}
                    </span>
                    <span className="renewal-offer__price--old">
                      {quote?.currency}
                      {quote?.original_price?.formatted_amount}
                    </span>
                  </div>
                  <p className="renewal-offer__pricing-period">
                    {quote?.current_plan_interval === "month" ? (
                      <span>
                        per year{" "}
                        <strong>
                          ({quote?.currency}
                          {quote?.monthly_amount} * 12 months payment plan)
                        </strong>
                      </span>
                    ) : (
                      <strong>per year</strong>
                    )}
                  </p>
                  <p className="">
                    {quote?.current_plan_interval === "year" && (
                      <Link
                        to="/passes/month/pay"
                        target="_blank"
                        className="renewal-offer__pricing-link"
                      >
                        Switch to monthly payment plan
                      </Link>
                    )}
                  </p>
                </div>

                <div className="renewal-offer__features">
                  <div className="renewal-offer__feature">
                    <div className="renewal-offer__feature-icon renewal-offer__feature-icon--green">
                      <Check size={16} />
                    </div>
                    <div className="renewal-offer__feature-content">
                      <h4 className="renewal-offer__feature-title">
                        No forms to fill
                      </h4>
                      <p className="renewal-offer__feature-description">
                        We already have your details safe and sound.
                      </p>
                    </div>
                  </div>

                  <div className="renewal-offer__feature">
                    <div className="renewal-offer__feature-icon renewal-offer__feature-icon--blue">
                      <Calendar size={16} />
                    </div>
                    <div className="renewal-offer__feature-content">
                      <h4 className="renewal-offer__feature-title">
                        Nothing to pay today
                      </h4>
                      <p className="renewal-offer__feature-description">
                        Your card won't be charged until your current pass
                        expires on{" "}
                        <span className="renewal-offer__feature-highlight">
                          {subscription?.valid_till}
                        </span>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="renewal-offer__feature">
                    <div className="renewal-offer__feature-icon renewal-offer__feature-icon--navy">
                      <GraduationCap size={16} />
                    </div>
                    <div className="renewal-offer__feature-content">
                      <h4 className="renewal-offer__feature-title">
                        Uninterrupted joy
                      </h4>
                      <p className="renewal-offer__feature-description">
                        Keep access to all current courses plus the upcoming AI
                        Leadership masterclass.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  className="renewal-offer__button"
                  onClick={handleRenewalOffer}
                >
                  {submitting ? (
                    <Loader
                      active
                      inline
                      inverted
                      size="small"
                      style={{ color: "#fff" }}
                    />
                  ) : (
                    "Yes, lock in my discount"
                  )}
                </button>

                <p className="renewal-offer__disclaimer">
                  By clicking above, your annual subscription will automatically
                  renew on {subscription?.valid_till}.
                </p>
              </div>
            </div>
          ) : (
            <div className="renewal-offer__success-view">
              <div className="renewal-offer__success-icon">
                <Check size={36} />
              </div>

              <h1 className="renewal-offer__success-title">
                Splendid! You're all set.
              </h1>
              <p className="renewal-offer__success-text">
                Your discount is locked in for {subscription?.valid_till}. We've
                sent a confirmation email for your records.
                <br />
                Now, go learn something new.
              </p>

              <Link to="/profile" className="renewal-offer__success-button">
                Take me to my dashboard
              </Link>
            </div>
          )}
        </main>
      )}
    </div>
  );
};
